import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  BarChart,
  Bar,
} from "recharts";
import MultiSelectDropdown from "../../MultiSelectDropdown";

const COMMANDER_FILTER_OPTIONS = [
  { value: "primary", label: "Primary", kind: "cityRole" },
  { value: "secondary", label: "Secondary", kind: "cityRole" },
  { value: "farm", label: "Farm", kind: "cityRole" },
  { value: "active", label: "Active", kind: "status" },
  { value: "retired", label: "Retired", kind: "status" },
];

function AnalyticsHeading({ title, info }) {
  return (
    <h4 className="events-analytics-heading">
      <span>{title}</span>
      {info ? (
        <span
          className="events-analytics-info-icon"
          title={info}
          aria-label={info}
          role="img"
        >
          i
        </span>
      ) : null}
    </h4>
  );
}

export default function EventAnalyticsPanel({
  isSuperUser,
  analyticsFilters,
  setAnalyticsFilters,
  analyticsSearch,
  setAnalyticsSearch,
  eventTypeMeta,
  analyticsNationOptions,
  analyticsAllianceOptions,
  loadParticipationAnalytics,
  isAnalyticsLoading,
  analyticsData,
  analyticsHeatmapRows,
  analyticsCommanderRows,
  formatEventDate,
}) {
  const eventTypeLabelById = useMemo(() => {
    const out = {};
    (eventTypeMeta?.eventTypes || []).forEach((type) => {
      out[String(type?.id || "")] = type?.label || type?.id || "Event";
    });
    return out;
  }, [eventTypeMeta]);

  const availableEvents = useMemo(() => {
    const rows = Array.isArray(analyticsData?.availableEvents)
      ? analyticsData.availableEvents
      : Array.isArray(analyticsData?.events)
        ? analyticsData.events
        : [];
    return rows
      .map((row) => {
        const eventTypeId = String(row?.eventType || "");
        const typeLabel = String(eventTypeLabelById[eventTypeId] || eventTypeId || "Event");
        const dateLabel = row?.startsAt ? formatEventDate(row.startsAt) : "No date";
        const label = String(row?.displayLabel || row?.name || `${typeLabel} ${dateLabel}`).trim();
        return {
          _id: String(row?._id || ""),
          label,
        };
      })
      .filter((row) => Boolean(row._id));
  }, [analyticsData, eventTypeLabelById, formatEventDate]);

  const allianceEventRows = useMemo(() => {
    return Array.isArray(analyticsData?.allianceEventStats)
      ? analyticsData.allianceEventStats
      : [];
  }, [analyticsData]);

  const weeklyTrendRows = useMemo(() => {
    return Array.isArray(analyticsData?.weeklyTrend)
      ? analyticsData.weeklyTrend
      : [];
  }, [analyticsData]);

  const filteredCommanderRows = useMemo(
    () => (Array.isArray(analyticsCommanderRows) ? analyticsCommanderRows : []),
    [analyticsCommanderRows],
  );

  const filteredCommanderCount = useMemo(() => {
    const ids = new Set(
      filteredCommanderRows
        .map((row) => String(row?.commanderId || ""))
        .filter(Boolean),
    );
    return ids.size;
  }, [filteredCommanderRows]);

  const activeCommanderCount = useMemo(() => {
    const ids = new Set();
    filteredCommanderRows.forEach((row) => {
      const commanderId = String(row?.commanderId || "");
      if (!commanderId) return;
      const hasEvent = Array.isArray(row?.eventIds) && row.eventIds.length > 0;
      if (hasEvent) ids.add(commanderId);
    });
    return ids.size;
  }, [filteredCommanderRows]);

  const chartCommanderRows = useMemo(
    () => filteredCommanderRows.slice(0, 120),
    [filteredCommanderRows],
  );

  const commanderQuadrantRows = useMemo(() => {
    return chartCommanderRows.map((row) => {
      const commanderName = String(row?.name || row?.commanderName || row?.commanderId || "Unknown");
      return {
        commanderId: row.commanderId,
        name: commanderName,
        allianceName: row.allianceName,
        participationPct: Number((Number(row.participationRate || 0) * 100).toFixed(2)),
        averageNormalizedScorePct: Number(row.averageNormalizedScorePerEventPct || 0),
        averageScoreRaw: Number(row.averageScorePerEvent || 0),
        totalScore: Number(row.totalScore || 0),
      };
    });
  }, [chartCommanderRows]);

  const allianceBenchmarkRows = useMemo(() => {
    const byAlliance = new Map();
    allianceEventRows.forEach((row) => {
      const key = String(row?.allianceId || "");
      if (!key) return;
      const existing = byAlliance.get(key) || {
        allianceId: key,
        allianceName: row?.allianceName || key,
        totalEventCount: 0,
        weightedParticipantSum: 0,
      };
      const count = Number(row?.eventCount || 0);
      const avgParticipants = Number(row?.averageParticipants || 0);
      existing.totalEventCount += count;
      existing.weightedParticipantSum += avgParticipants * count;
      byAlliance.set(key, existing);
    });

    return Array.from(byAlliance.values())
      .map((row) => ({
        ...row,
        averageParticipants: Number(
          row.totalEventCount > 0
            ? (row.weightedParticipantSum / row.totalEventCount).toFixed(2)
            : 0,
        ),
      }))
      .sort((a, b) => Number(b.totalEventCount || 0) - Number(a.totalEventCount || 0))
      .slice(0, 20);
  }, [allianceEventRows]);

  const participationHistogramRows = useMemo(() => {
    const bins = [
      { key: "0-20", min: 0, max: 0.2, count: 0 },
      { key: "20-40", min: 0.2, max: 0.4, count: 0 },
      { key: "40-60", min: 0.4, max: 0.6, count: 0 },
      { key: "60-80", min: 0.6, max: 0.8, count: 0 },
      { key: "80-100", min: 0.8, max: 1.00001, count: 0 },
    ];

    chartCommanderRows.forEach((row) => {
      const rate = Number(row?.participationRate || 0);
      const bin = bins.find((item) => rate >= item.min && rate < item.max);
      if (bin) bin.count += 1;
    });

    return bins.map((row) => ({ bucket: row.key, count: row.count }));
  }, [chartCommanderRows]);
  const dayTimeParticipationHeatmap = useMemo(() => {
    const payload = analyticsData?.dayTimeParticipationHeatmap || {};
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    const timeSlots = Array.isArray(payload?.timeSlots) ? payload.timeSlots : [];
    return {
      rows,
      timeSlots,
      maxAverageParticipants: Number(payload?.maxAverageParticipants || 0),
      totalEventsConsidered: Number(payload?.totalEventsConsidered || 0),
      totalUniqueDateTimeWindows: Number(payload?.totalUniqueDateTimeWindows || 0),
    };
  }, [analyticsData]);

  const selectedNationValues = useMemo(() => {
    const selected = new Set((analyticsFilters?.nationIds || []).map((id) => String(id || "")));
    return (analyticsNationOptions || []).filter((option) => selected.has(String(option?._id || "")));
  }, [analyticsFilters?.nationIds, analyticsNationOptions]);

  const selectedAllianceValues = useMemo(() => {
    const selected = new Set((analyticsFilters?.allianceIds || []).map((id) => String(id || "")));
    return (analyticsAllianceOptions || []).filter((option) => selected.has(String(option?._id || "")));
  }, [analyticsFilters?.allianceIds, analyticsAllianceOptions]);

  const eventTypeOptions = useMemo(() => {
    return (eventTypeMeta?.eventTypes || [])
      .map((type) => ({
        id: String(type?.id || ""),
        label: String(type?.label || type?.id || "Event"),
      }))
      .filter((row) => Boolean(row.id));
  }, [eventTypeMeta]);

  const selectedEventTypeValues = useMemo(() => {
    const selected = new Set((analyticsFilters?.eventTypes || []).map((id) => String(id || "")));
    return eventTypeOptions.filter((option) => selected.has(String(option?.id || "")));
  }, [analyticsFilters?.eventTypes, eventTypeOptions]);

  const selectedCommanderFilterValues = useMemo(() => {
    const selected = new Set([
      ...(analyticsFilters?.cityRoles || []).map((value) => String(value || "")),
      ...(analyticsFilters?.statuses || []).map((value) => String(value || "")),
    ]);
    return COMMANDER_FILTER_OPTIONS.filter((option) => selected.has(String(option.value || "")));
  }, [analyticsFilters?.cityRoles, analyticsFilters?.statuses]);

  const selectedEventValues = useMemo(() => {
    const selected = new Set((analyticsFilters?.eventIds || []).map((id) => String(id || "")));
    return availableEvents.filter((option) => selected.has(String(option?._id || "")));
  }, [analyticsFilters?.eventIds, availableEvents]);

  const [expandedChartKey, setExpandedChartKey] = useState("");
  const [isParticipationHeatmapCollapsed, setIsParticipationHeatmapCollapsed] = useState(true);
  const [isCommanderParticipationCollapsed, setIsCommanderParticipationCollapsed] = useState(true);
  const [heatmapSort, setHeatmapSort] = useState({ key: "total", direction: "desc" });
  const [commanderSort, setCommanderSort] = useState({ key: "totalScore", direction: "desc" });
  const axisLabelStyle = { fill: "#4a6076", fontSize: 12, fontWeight: 600 };
  const heatmapColumns = Array.isArray(analyticsData?.heatmap?.columns) ? analyticsData.heatmap.columns : [];

  const toggleSort = (currentSort, setSort, nextKey) => {
    setSort((prev) => {
      if (prev?.key === nextKey) {
        return {
          key: nextKey,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: nextKey, direction: "desc" };
    });
  };

  const renderSortMarker = (sort, key) => {
    if (sort?.key !== key) return "↕";
    return sort.direction === "asc" ? "↑" : "↓";
  };

  const sortedHeatmapRows = useMemo(() => {
    const rows = Array.isArray(analyticsHeatmapRows) ? [...analyticsHeatmapRows] : [];
    const key = String(heatmapSort?.key || "total");
    const direction = heatmapSort?.direction === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      if (key === "commander") {
        const left = `${String(a?.name || "")} ${String(a?.allianceName || "")}`.trim().toLowerCase();
        const right = `${String(b?.name || "")} ${String(b?.allianceName || "")}`.trim().toLowerCase();
        return left.localeCompare(right) * direction;
      }

      if (key.startsWith("cell:")) {
        const columnKey = key.slice("cell:".length);
        const left = Number(a?.cells?.[columnKey] || 0);
        const right = Number(b?.cells?.[columnKey] || 0);
        if (left !== right) return (left - right) * direction;
      } else {
        const left = Number(a?.total || 0);
        const right = Number(b?.total || 0);
        if (left !== right) return (left - right) * direction;
      }

      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
    return rows;
  }, [analyticsHeatmapRows, heatmapSort]);

  const sortedCommanderRows = useMemo(() => {
    const rows = Array.isArray(analyticsCommanderRows) ? [...analyticsCommanderRows] : [];
    const key = String(commanderSort?.key || "totalScore");
    const direction = commanderSort?.direction === "asc" ? 1 : -1;

    const getValue = (row) => {
      switch (key) {
        case "name":
          return String(row?.name || "").toLowerCase();
        case "allianceName":
          return String(row?.allianceName || "").toLowerCase();
        case "eligibleEventsCount":
          return Number(row?.eligibleEventsCount || 0);
        case "eventCount":
          return Number(row?.eventCount || 0);
        case "participationRate":
          return Number(row?.participationRate || 0);
        case "averageNormalizedScorePerEventPct":
          return Number(row?.averageNormalizedScorePerEventPct || 0);
        case "averageScorePerEvent":
          return Number(row?.averageScorePerEvent || 0);
        case "entryCount":
          return Number(row?.entryCount || 0);
        case "totalScore":
        default:
          return Number(row?.totalScore || 0);
      }
    };

    rows.sort((a, b) => {
      const left = getValue(a);
      const right = getValue(b);
      if (typeof left === "string" && typeof right === "string") {
        return left.localeCompare(right) * direction;
      }
      if (left !== right) {
        return (Number(left) - Number(right)) * direction;
      }
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
    return rows;
  }, [analyticsCommanderRows, commanderSort]);

  const renderCommanderQuadrantChart = (height = 320) => (
    <div className="events-analytics-chart-wrap" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 36, right: 18, bottom: 42, left: 52 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="participationPct"
            name="Participation %"
            domain={[0, 100]}
            tickFormatter={(value) => `${value}%`}
            label={{ value: "Participation Rate (%)", position: "bottom", offset: 10, style: axisLabelStyle }}
          />
          <YAxis
            type="number"
            dataKey="averageNormalizedScorePct"
            name="Normalized Score %"
            domain={[0, 100]}
            width={88}
            tickFormatter={(value) => `${Number(value || 0).toFixed(0)}%`}
            label={{ value: "Avg Normalized Score (%)", angle: -90, position: "left", style: axisLabelStyle }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const point = payload[0]?.payload || {};
              return (
                <div className="events-analytics-tooltip">
                  <div><strong>{String(point?.name || point?.commanderId || "Commander")}</strong></div>
                  <div>{String(point?.allianceName || "-")}</div>
                  <div>Participation %: {Number(point?.participationPct || 0).toFixed(2)}</div>
                  <div>Avg Normalized Score %: {Number(point?.averageNormalizedScorePct || 0).toFixed(2)}</div>
                  <div>Avg Raw Score: {Number(point?.averageScoreRaw || 0).toLocaleString()}</div>
                  <div>Total Score: {Number(point?.totalScore || 0).toLocaleString()}</div>
                </div>
              );
            }}
          />
          <Legend verticalAlign="top" align="right" wrapperStyle={{ top: 0 }} />
          <Scatter name="Commanders" data={commanderQuadrantRows} fill="#0f6b8a" />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );

  const renderWeeklyTrendChart = (height = 320) => (
    <div className="events-analytics-chart-wrap" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={weeklyTrendRows} margin={{ top: 36, right: 44, bottom: 44, left: 54 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="weekStart" label={{ value: "Week Start (UTC)", position: "bottom", offset: 10, style: axisLabelStyle }} />
          <YAxis yAxisId="left" label={{ value: "Entries / Active Commanders", angle: -90, position: "left", style: axisLabelStyle }} />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={(value) => Number(value || 0).toLocaleString()}
            width={86}
            label={{ value: "Total Score", angle: 90, position: "right", style: axisLabelStyle }}
          />
          <Tooltip formatter={(value) => Number(value).toLocaleString()} />
          <Legend verticalAlign="top" align="right" wrapperStyle={{ top: 0 }} />
          <Line yAxisId="left" type="monotone" dataKey="entries" name="Entries" stroke="#0f6b8a" strokeWidth={2} dot={false} />
          <Line yAxisId="left" type="monotone" dataKey="activeCommanders" name="Active Commanders" stroke="#4d84b8" strokeWidth={2} dot={false} />
          <Line yAxisId="right" type="monotone" dataKey="totalScore" name="Total Score" stroke="#16a34a" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  const renderAllianceBenchmarkChart = (height = 320) => (
    <div className="events-analytics-chart-wrap" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={allianceBenchmarkRows} margin={{ top: 36, right: 44, bottom: 52, left: 50 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="allianceName"
            interval={0}
            angle={-25}
            textAnchor="end"
            height={72}
            label={{ value: "Alliance", position: "bottom", offset: 18, style: axisLabelStyle }}
          />
          <YAxis yAxisId="left" label={{ value: "Event Count", angle: -90, position: "left", style: axisLabelStyle }} />
          <YAxis
            yAxisId="right"
            orientation="right"
            width={84}
            label={{ value: "Avg Participants", angle: 90, position: "right", style: axisLabelStyle }}
          />
          <Tooltip formatter={(value) => Number(value).toLocaleString()} />
          <Legend verticalAlign="top" align="right" wrapperStyle={{ top: 0 }} />
          <Bar yAxisId="left" dataKey="totalEventCount" name="Event Count" fill="#0f6b8a" />
          <Bar yAxisId="right" dataKey="averageParticipants" name="Avg Participants" fill="#5aa6c7" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderParticipationHistogramChart = (height = 320) => (
    <div className="events-analytics-chart-wrap" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={participationHistogramRows} margin={{ top: 16, right: 12, bottom: 42, left: 52 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bucket" label={{ value: "Participation Rate Bucket (%)", position: "bottom", offset: 10, style: axisLabelStyle }} />
          <YAxis label={{ value: "Commander Count", angle: -90, position: "left", style: axisLabelStyle }} />
          <Tooltip formatter={(value) => Number(value).toLocaleString()} />
          <Bar dataKey="count" name="Commanders" fill="#4d84b8" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const expandedChartConfig = (() => {
    if (expandedChartKey === "commanderQuadrant" && commanderQuadrantRows.length) {
      return {
        title: "Commander Participation vs Normalized Score",
        info: "Each score is normalized against the top score for that event type (within current analytics scope), then averaged per commander.",
        render: () => renderCommanderQuadrantChart(560),
      };
    }
    if (expandedChartKey === "weeklyTrend" && weeklyTrendRows.length) {
      return {
        title: "Weekly Activity Trend",
        info: "Entries = submitted rows, Active Commanders = peak unique commanders for that week, Total Score = sum of submitted scores.",
        render: () => renderWeeklyTrendChart(560),
      };
    }
    if (expandedChartKey === "allianceBenchmark" && allianceBenchmarkRows.length) {
      return {
        title: "Alliance Benchmarking",
        info: "Compares alliance event coverage and average participant volume.",
        render: () => renderAllianceBenchmarkChart(560),
      };
    }
    if (expandedChartKey === "participationHistogram" && participationHistogramRows.length) {
      return {
        title: "Participation Distribution",
        info: "Shows how many commanders fall into each participation-rate bucket.",
        render: () => renderParticipationHistogramChart(560),
      };
    }
    return null;
  })();

  return (
    <div className="events-card events-panel-shell events-analytics-panel">
      <div className="events-panel-header">
        <AnalyticsHeading
          title="Participation Analytics"
          info="Filter events and compare commander participation and contribution across selected scope."
        />
        <span>Cross-event commander participation and score trends.</span>
      </div>

      <div className="events-grid events-analytics-filters">
        <div className="events-analytics-multi-filter">
          <MultiSelectDropdown
            label="Event Types"
            options={eventTypeOptions}
            selectedValues={selectedEventTypeValues}
            onChange={(nextOptions) => {
              const nextEventTypes = Array.from(
                new Set((nextOptions || []).map((option) => String(option?.id || "")).filter(Boolean)),
              );
              setAnalyticsFilters((prev) => ({ ...prev, eventTypes: nextEventTypes }));
            }}
            getOptionLabel={(option) => String(option?.label || option?.id || "")}
            getOptionValue={(option) => String(option?.id || "")}
            helperText="Empty selection means all event types."
          />
        </div>

        <label>
          Start Date
          <input
            type="date"
            value={analyticsFilters.startDate}
            onChange={(e) => setAnalyticsFilters((prev) => ({ ...prev, startDate: e.target.value }))}
          />
        </label>

        <label>
          End Date
          <input
            type="date"
            value={analyticsFilters.endDate}
            onChange={(e) => setAnalyticsFilters((prev) => ({ ...prev, endDate: e.target.value }))}
          />
        </label>

        {isSuperUser && (
          <div className="events-analytics-multi-filter">
            <MultiSelectDropdown
              label="Nations"
              options={analyticsNationOptions || []}
              selectedValues={selectedNationValues}
              onChange={(nextOptions) => {
                const nextNationIds = Array.from(
                  new Set((nextOptions || []).map((option) => String(option?._id || "")).filter(Boolean)),
                );
                setAnalyticsFilters((prev) => ({
                  ...prev,
                  nationIds: nextNationIds,
                }));
              }}
              getOptionLabel={(option) => String(option?.label || option?.name || option?._id || "")}
              getOptionValue={(option) => String(option?._id || "")}
              helperText="Defaults to your nation if none selected."
            />
          </div>
        )}

        <div className="events-analytics-multi-filter">
          <MultiSelectDropdown
            label="Alliances"
            options={analyticsAllianceOptions || []}
            selectedValues={selectedAllianceValues}
            onChange={(nextOptions) => {
              const nextAllianceIds = Array.from(
                new Set((nextOptions || []).map((option) => String(option?._id || "")).filter(Boolean)),
              );
              setAnalyticsFilters((prev) => ({
                ...prev,
                allianceIds: nextAllianceIds,
              }));
            }}
            getOptionLabel={(option) => String(option?.label || option?._id || "")}
            getOptionValue={(option) => String(option?._id || "")}
            helperText="Defaults to all alliances in selected nation scope."
          />
        </div>

        <div className="events-analytics-multi-filter">
          <MultiSelectDropdown
            label="Commander Filters"
            options={COMMANDER_FILTER_OPTIONS}
            selectedValues={selectedCommanderFilterValues}
            onChange={(nextOptions) => {
              const cityRoleSelections = (nextOptions || [])
                .filter((option) => String(option?.kind || "") === "cityRole")
                .map((option) => String(option?.value || ""))
                .filter(Boolean);
              const statusSelections = (nextOptions || [])
                .filter((option) => String(option?.kind || "") === "status")
                .map((option) => String(option?.value || ""))
                .filter(Boolean);

              setAnalyticsFilters((prev) => ({
                ...prev,
                cityRoles: cityRoleSelections.length > 0 ? cityRoleSelections : ["primary"],
                statuses: statusSelections.length > 0 ? statusSelections : ["active"],
              }));
            }}
            getOptionLabel={(option) => String(option?.label || option?.value || "")}
            getOptionValue={(option) => String(option?.value || "")}
            helperText="Default: Primary + Active"
          />
        </div>

        <label className="events-analytics-checkbox-filter">
          <input
            type="checkbox"
            checked={Boolean(analyticsFilters.includeLegacyMembers)}
            onChange={(e) => setAnalyticsFilters((prev) => ({
              ...prev,
              includeLegacyMembers: e.target.checked,
            }))}
          />
          <span>Include legacy members</span>
        </label>

        <div className="events-analytics-multi-filter">
          <MultiSelectDropdown
            label="Events"
            options={availableEvents}
            selectedValues={selectedEventValues}
            onChange={(nextOptions) => {
              const nextEventIds = Array.from(
                new Set((nextOptions || []).map((option) => String(option?._id || "")).filter(Boolean)),
              );
              setAnalyticsFilters((prev) => ({
                ...prev,
                eventIds: nextEventIds,
              }));
            }}
            getOptionLabel={(option) => String(option?.label || option?._id || "")}
            getOptionValue={(option) => String(option?._id || "")}
            helperText="Empty selection means all available events."
          />
        </div>

        <label>
          Commander Search
          <input
            value={analyticsSearch}
            onChange={(e) => setAnalyticsSearch(e.target.value)}
            placeholder="Filter by commander/alliance"
          />
        </label>
      </div>

      <div className="events-actions-row">
        <button className="events-primary-btn" onClick={loadParticipationAnalytics}>
          {isAnalyticsLoading ? "Refreshing..." : "Refresh Analytics"}
        </button>
      </div>

      {analyticsData && (
        <>
          <div className="events-analytics-summary-grid">
            <div className="events-analytics-stat"><span>Events</span><strong>{Number(analyticsData?.summary?.events || 0).toLocaleString()}</strong></div>
            <div className="events-analytics-stat"><span>Commanders</span><strong>{Number(filteredCommanderCount || 0).toLocaleString()}</strong></div>
            <div className="events-analytics-stat"><span>Active Commanders</span><strong>{Number(activeCommanderCount || 0).toLocaleString()}</strong></div>
            <div className="events-analytics-stat"><span>Entries</span><strong>{Number(analyticsData?.summary?.entries || 0).toLocaleString()}</strong></div>
            <div className="events-analytics-stat"><span>Total Score</span><strong>{Number(analyticsData?.summary?.totalScore || 0).toLocaleString()}</strong></div>
            <div className="events-analytics-stat"><span>Avg Participation</span><strong>{`${Math.round(Number(analyticsData?.summary?.averageParticipationRate || 0) * 100)}%`}</strong></div>
            <div className="events-analytics-stat"><span>Avg Score / Event</span><strong>{Number(analyticsData?.summary?.averageScorePerEvent || 0).toLocaleString()}</strong></div>
            <div className="events-analytics-stat"><span>Avg Normalized Score</span><strong>{`${Number(Number(analyticsData?.summary?.averageNormalizedScorePerEvent || 0) * 100).toFixed(1)}%`}</strong></div>
          </div>

          <div className="events-grid events-analytics-chart-grid">
            {!!commanderQuadrantRows.length && (
              <div className="events-analytics-chart-card">
                <button
                  type="button"
                  className="events-analytics-expand-btn"
                  onClick={() => setExpandedChartKey("commanderQuadrant")}
                  title="Expand chart"
                  aria-label="Expand chart"
                >
                  ⤢
                </button>
                <AnalyticsHeading
                  title="Commander Participation vs Normalized Score"
                  info="Each score is normalized against the top score for that event type (within current analytics scope), then averaged per commander."
                />
                {renderCommanderQuadrantChart(320)}
              </div>
            )}

            {!!weeklyTrendRows.length && (
              <div className="events-analytics-chart-card">
                <button
                  type="button"
                  className="events-analytics-expand-btn"
                  onClick={() => setExpandedChartKey("weeklyTrend")}
                  title="Expand chart"
                  aria-label="Expand chart"
                >
                  ⤢
                </button>
                <AnalyticsHeading
                  title="Weekly Activity Trend"
                  info="Entries = submitted rows, Active Commanders = peak unique commanders for that week, Total Score = sum of submitted scores."
                />
                {renderWeeklyTrendChart(320)}
              </div>
            )}

            {!!allianceBenchmarkRows.length && (
              <div className="events-analytics-chart-card">
                <button
                  type="button"
                  className="events-analytics-expand-btn"
                  onClick={() => setExpandedChartKey("allianceBenchmark")}
                  title="Expand chart"
                  aria-label="Expand chart"
                >
                  ⤢
                </button>
                <AnalyticsHeading
                  title="Alliance Benchmarking"
                  info="Compares alliance event coverage and average participant volume."
                />
                {renderAllianceBenchmarkChart(320)}
              </div>
            )}

            {!!participationHistogramRows.length && (
              <div className="events-analytics-chart-card">
                <button
                  type="button"
                  className="events-analytics-expand-btn"
                  onClick={() => setExpandedChartKey("participationHistogram")}
                  title="Expand chart"
                  aria-label="Expand chart"
                >
                  ⤢
                </button>
                <AnalyticsHeading
                  title="Participation Distribution"
                  info="Shows how many commanders fall into each participation-rate bucket."
                />
                {renderParticipationHistogramChart(320)}
              </div>
            )}
          </div>

          {expandedChartConfig && (
            <div className="events-analytics-chart-modal-backdrop" onClick={() => setExpandedChartKey("")}>
              <div className="events-analytics-chart-modal" onClick={(e) => e.stopPropagation()}>
                <div className="events-analytics-chart-modal-header">
                  <AnalyticsHeading title={expandedChartConfig.title} info={expandedChartConfig.info} />
                  <button
                    type="button"
                    className="events-analytics-chart-modal-close"
                    onClick={() => setExpandedChartKey("")}
                  >
                    Close
                  </button>
                </div>
                {expandedChartConfig.render()}
              </div>
            </div>
          )}

          {!!dayTimeParticipationHeatmap.rows.length && !!dayTimeParticipationHeatmap.timeSlots.length && (
            <div className="events-analytics-chart-card events-analytics-full-width-card">
              <AnalyticsHeading
                title="Weekly Server-Time Participation Potential (All Alliances)"
                info="Average active commanders in each weekday/server-time slot across selected events, aggregated regardless of alliance."
              />
              <div className="events-analytics-inline-note">
                Based on {Number(dayTimeParticipationHeatmap.totalEventsConsidered || 0).toLocaleString()} selected events
                across {Number(dayTimeParticipationHeatmap.totalUniqueDateTimeWindows || 0).toLocaleString()} unique date/time windows.
              </div>
              <div className="events-table-wrap events-analytics-daytime-wrap">
                <table className="events-table events-analytics-daytime-table">
                  <thead>
                    <tr>
                      <th>Weekday</th>
                      {dayTimeParticipationHeatmap.timeSlots.map((timeSlot) => (
                        <th key={timeSlot}>{timeSlot}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dayTimeParticipationHeatmap.rows.map((row) => (
                      <tr key={String(row?.day || row?.dayIndex || "")}>
                        <td>{row?.day || "Unknown"}</td>
                        {dayTimeParticipationHeatmap.timeSlots.map((timeSlot) => {
                          const value = Number(row?.cells?.[timeSlot] || 0);
                          const eventCount = Number(row?.eventCounts?.[timeSlot] || 0);
                          const ratio = dayTimeParticipationHeatmap.maxAverageParticipants > 0
                            ? value / dayTimeParticipationHeatmap.maxAverageParticipants
                            : 0;
                          const background = value > 0
                            ? `rgba(14, 116, 144, ${Math.min(0.92, 0.16 + ratio * 0.76)})`
                            : undefined;
                          return (
                            <td
                              key={`${row?.day || row?.dayIndex || "day"}-${timeSlot}`}
                              className="events-analytics-daytime-cell"
                              style={{ background }}
                              title={eventCount > 0
                                ? `Average active commanders: ${value.toLocaleString()} (from ${eventCount} unique date/time window${eventCount === 1 ? "" : "s"})`
                                : "No events in this slot"}
                            >
                              {value > 0 ? value.toFixed(1) : ""}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!!analyticsHeatmapRows.length && (
            <div className="events-collapsible-card">
              <button
                className="events-collapse-toggle"
                onClick={() => setIsParticipationHeatmapCollapsed((prev) => !prev)}
              >
                <span>Participation Heatmap (Commander x Event Type)</span>
                <span>{isParticipationHeatmapCollapsed ? "+" : "-"}</span>
              </button>
              {!isParticipationHeatmapCollapsed && (
                <div className="events-table-wrap">
                  <AnalyticsHeading
                    title="Participation Heatmap (Commander x Event Type)"
                    info="Cell intensity reflects how often a commander appears in each event type. Click table headers to sort."
                  />
                  <table className="events-table events-analytics-heatmap-table">
                    <thead>
                      <tr>
                        <th>
                          <button
                            type="button"
                            className="events-analytics-sort-btn"
                            onClick={() => toggleSort(heatmapSort, setHeatmapSort, "commander")}
                          >
                            Commander {renderSortMarker(heatmapSort, "commander")}
                          </button>
                        </th>
                        {heatmapColumns.map((column) => (
                          <th key={column}>
                            <button
                              type="button"
                              className="events-analytics-sort-btn"
                              onClick={() => toggleSort(heatmapSort, setHeatmapSort, `cell:${column}`)}
                            >
                              {column} {renderSortMarker(heatmapSort, `cell:${column}`)}
                            </button>
                          </th>
                        ))}
                        <th>
                          <button
                            type="button"
                            className="events-analytics-sort-btn"
                            onClick={() => toggleSort(heatmapSort, setHeatmapSort, "total")}
                          >
                            Total {renderSortMarker(heatmapSort, "total")}
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedHeatmapRows.map((row) => (
                        <tr key={row.commanderId}>
                          <td>{row.name}{row.allianceName ? ` (${row.allianceName})` : ""}</td>
                          {heatmapColumns.map((column) => {
                            const value = Number(row?.cells?.[column] || 0);
                            return (
                              <td
                                key={`${row.commanderId}-${column}`}
                                className="events-analytics-heat-cell"
                                style={{ background: value ? `rgba(14, 116, 144, ${Math.min(0.9, 0.18 + value * 0.12)})` : undefined }}
                              >
                                {value || ""}
                              </td>
                            );
                          })}
                          <td>{Number(row.total || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="events-collapsible-card">
            <button
              className="events-collapse-toggle"
              onClick={() => setIsCommanderParticipationCollapsed((prev) => !prev)}
            >
              <span>Commander Participation</span>
              <span>{isCommanderParticipationCollapsed ? "+" : "-"}</span>
            </button>
            {!isCommanderParticipationCollapsed && (
              <div className="events-table-wrap">
                <AnalyticsHeading
                  title="Commander Participation"
                  info="Eligible Events = events with at least one result row for that commander. Participated = eligible events marked as participated. Click table headers to sort."
                />
                <table className="events-table">
                  <thead>
                    <tr>
                      <th><button type="button" className="events-analytics-sort-btn" onClick={() => toggleSort(commanderSort, setCommanderSort, "name")}>Commander {renderSortMarker(commanderSort, "name")}</button></th>
                      <th><button type="button" className="events-analytics-sort-btn" onClick={() => toggleSort(commanderSort, setCommanderSort, "allianceName")}>Alliance {renderSortMarker(commanderSort, "allianceName")}</button></th>
                      <th><button type="button" className="events-analytics-sort-btn" onClick={() => toggleSort(commanderSort, setCommanderSort, "eligibleEventsCount")}>Eligible Events {renderSortMarker(commanderSort, "eligibleEventsCount")}</button></th>
                      <th><button type="button" className="events-analytics-sort-btn" onClick={() => toggleSort(commanderSort, setCommanderSort, "eventCount")}>Participated {renderSortMarker(commanderSort, "eventCount")}</button></th>
                      <th><button type="button" className="events-analytics-sort-btn" onClick={() => toggleSort(commanderSort, setCommanderSort, "participationRate")}>Participation (eligible) {renderSortMarker(commanderSort, "participationRate")}</button></th>
                      <th><button type="button" className="events-analytics-sort-btn" onClick={() => toggleSort(commanderSort, setCommanderSort, "averageNormalizedScorePerEventPct")}>Avg Normalized / Event {renderSortMarker(commanderSort, "averageNormalizedScorePerEventPct")}</button></th>
                      <th><button type="button" className="events-analytics-sort-btn" onClick={() => toggleSort(commanderSort, setCommanderSort, "averageScorePerEvent")}>Avg Score / Event {renderSortMarker(commanderSort, "averageScorePerEvent")}</button></th>
                      <th><button type="button" className="events-analytics-sort-btn" onClick={() => toggleSort(commanderSort, setCommanderSort, "entryCount")}>Entries {renderSortMarker(commanderSort, "entryCount")}</button></th>
                      <th><button type="button" className="events-analytics-sort-btn" onClick={() => toggleSort(commanderSort, setCommanderSort, "totalScore")}>Total Score {renderSortMarker(commanderSort, "totalScore")}</button></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCommanderRows.map((row) => (
                      <tr key={row.commanderId}>
                        <td>{row.name || row.commanderId}</td>
                        <td>{row.allianceName || "-"}</td>
                        <td>{Number(row.eligibleEventsCount || 0).toLocaleString()}</td>
                        <td>{Number(row.eventCount || 0).toLocaleString()}</td>
                        <td>{`${Math.round(Number(row.participationRate || 0) * 100)}%`}</td>
                        <td>{`${Number(row.averageNormalizedScorePerEventPct || 0).toFixed(2)}%`}</td>
                        <td>{Number(row.averageScorePerEvent || 0).toLocaleString()}</td>
                        <td>{Number(row.entryCount || 0).toLocaleString()}</td>
                        <td>{Number(row.totalScore || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
